"use client";

import { useCallback, useLayoutEffect, useRef, useState, useTransition } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { toast } from "sonner";
import { Loader2, Pencil, ThumbsUp, Trash2 } from "lucide-react";
import BlockEditor from "@/components/BlockEditor";
import MembershipBadge from "@/components/MembershipBadge";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { MobileBottomToolRegistration } from "@/components/mobile-bottom-tools";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { uploadBlockEditorImages } from "@/lib/block-editor-image-upload";
import {
  COMMUNITY_TALK_PATH,
  TALK_LIMITS,
  formatTalkDateTime,
  isHotTalkPost,
  type TalkPost,
} from "@/lib/community-talk";
import { renderMixedContent } from "@/lib/renderContentUtils";
import {
  deleteTalkPost,
  recommendTalkPost,
  updateTalkPost,
} from "@/lib/server-actions/community-talk/posts";
import type { PassType } from "@/types/pass";

const fieldClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-[#333] focus:outline-none focus:ring-2 focus:ring-amber-400 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100";

/**
 * 잡담 글 본문 카드(JCG-3029). 제목(추천글 배지)·글쓴이·본문 + 추천 버튼 + 수정/삭제.
 * - 본문은 댓글과 같은 `renderMixedContent` 로 그리고, 수정도 같은 `BlockEditor` 를 쓴다.
 * - 수정/삭제 버튼은 본인·관리자에게만. 추천은 로그인 후. 최종 권한 판단은 server action.
 * - 모바일(<1024px)에서는 추천 버튼을 하단 dock 에도 등록한다(`추천 ▲N`, 본문 버튼과 같은 핸들러).
 */
export default function TalkPostCard({ post: initialPost }: { post: TalkPost }) {
  const router = useRouter();
  const { inShell } = useArticleShellSlots();
  const { isLoaded, isSignedIn, user } = useUser();
  const { isAdmin } = useAdminStatus();
  const [post, setPost] = useState(initialPost);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(initialPost.title);
  const [content, setContent] = useState(initialPost.content);
  const [isNotice, setIsNotice] = useState(initialPost.isNotice);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const [avatarError, setAvatarError] = useState(false);
  const imageFileMapRef = useRef<Map<string, File>>(new Map());

  const handleImageFilesChange = useCallback((nextMap: Map<string, File>) => {
    imageFileMapRef.current = nextMap;
  }, []);

  // Cache Components는 이탈한 페이지를 Activity로 숨긴다. 숨겨질 때 편집을 끝낸다.
  useLayoutEffect(() => {
    return () => {
      setEditing(false);
      setTitle("");
      setContent("");
      setIsNotice(false);
      setError(null);
      imageFileMapRef.current.clear();
    };
  }, []);

  const isOwner = isSignedIn === true && user?.id === post.clerkUserId;
  const canMutate = isOwner || isAdmin;

  const handleRecommend = () => {
    if (isLoaded && !isSignedIn) {
      toast.error("로그인 후 추천할 수 있어요.");
      return;
    }
    startTransition(async () => {
      const result = await recommendTalkPost(post.id);
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      setPost((prev) => ({ ...prev, recommendCount: result.recommendCount }));
      toast.success("추천했어요.");
    });
  };

  const handleSave = () => {
    setError(null);
    const contentAtSubmit = content;
    const imageFileMapAtSubmit = new Map(imageFileMapRef.current);
    startTransition(async () => {
      try {
        const processedContent = await uploadBlockEditorImages({
          content: contentAtSubmit,
          imageFileMap: imageFileMapAtSubmit,
          uploadEndpoint: "/api/upload-comment-image",
        });
        const result = await updateTalkPost(post.id, {
          title: title.trim(),
          content: processedContent.trim(),
          // 공지 토글은 관리자만 보낸다(서버도 관리자만 허용).
          ...(isAdmin ? { isNotice } : {}),
        });
        if (!result.success) {
          setError(result.error);
          return;
        }
        setPost(result.post);
        setContent(result.post.content);
        setIsNotice(result.post.isNotice);
        setEditing(false);
        toast.success("글을 수정했어요.");
        router.refresh();
      } catch (saveError) {
        console.error("잡담 글 수정 실패:", saveError);
        setError(saveError instanceof Error ? saveError.message : "글 수정에 실패했습니다.");
      }
    });
  };

  const handleDelete = () => {
    if (!window.confirm("이 글을 삭제할까요? 삭제하면 되돌릴 수 없어요.")) return;
    startTransition(async () => {
      const result = await deleteTalkPost(post.id);
      if (!result.success) {
        toast.error(result.error);
        return;
      }
      toast.success("글을 삭제했어요.");
      router.push(COMMUNITY_TALK_PATH);
      router.refresh();
    });
  };

  return (
    <article className="rounded-md border border-gray-200 bg-white dark:border-neutral-700 dark:bg-neutral-900/40">
      {editing ? (
        <div className="space-y-3 p-4 sm:p-5">
          <input
            type="text"
            value={title}
            maxLength={TALK_LIMITS.title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label="제목"
            className={`text-lg font-semibold ${fieldClass}`}
          />
          {isAdmin && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isNotice}
                onChange={(e) => setIsNotice(e.target.checked)}
                className="size-4 accent-amber-500"
              />
              <span className="font-semibold text-amber-900 dark:text-amber-200">공지사항</span>
              <span className="text-xs text-muted-foreground">(관리자 전용, 목록 맨 위 고정)</span>
            </label>
          )}
          <div className="rounded-md border border-gray-200 bg-white p-2 dark:border-neutral-600 dark:bg-neutral-800">
            <BlockEditor
              value={content}
              onChange={setContent}
              placeholder="내용"
              onImageFilesChange={handleImageFilesChange}
              maxImages={TALK_LIMITS.images}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setTitle(post.title);
                setContent(post.content);
                setIsNotice(post.isNotice);
                setError(null);
                imageFileMapRef.current.clear();
              }}
              disabled={isPending}
              className="rounded-md px-3 py-1.5 text-sm text-[#706E6E] hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending || title.trim().length === 0 || content.trim().length === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
            >
              {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
              저장
            </button>
          </div>
        </div>
      ) : (
        <>
          <header className="border-b border-gray-200 px-4 py-4 dark:border-neutral-700 sm:px-5">
            <h1 className="text-xl font-semibold leading-snug text-[#333] dark:text-neutral-100 sm:text-2xl">
              {post.isNotice && (
                <span className="mr-1.5 align-middle rounded bg-amber-500 px-1.5 py-0.5 text-xs font-semibold text-white dark:bg-amber-400 dark:text-amber-950">
                  공지
                </span>
              )}
              {!post.isNotice && isHotTalkPost(post) && (
                <span className="mr-1.5 align-middle rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                  추천글
                </span>
              )}
              {post.title}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#706E6E] dark:text-neutral-400">
              <span className="inline-flex items-center gap-1.5">
                <Image
                  src={post.profileImageUrl && !avatarError ? post.profileImageUrl : "/default_gamja.webp"}
                  alt=""
                  width={20}
                  height={20}
                  className="size-5 rounded-full object-cover"
                  onError={() => setAvatarError(true)}
                />
                <span className="font-medium text-[#333] dark:text-neutral-200">{post.author}</span>
                <MembershipBadge passType={(post.passType as PassType | null) ?? null} />
              </span>
              <time dateTime={post.createdAt}>{formatTalkDateTime(post.createdAt)}</time>
              <span>조회 {post.viewCount}</span>
              <span>추천 {post.recommendCount}</span>
              {post.contentEditedAt && <span>(수정됨)</span>}
            </div>
          </header>

          <div className="px-4 py-5 sm:px-5">
            <div className="break-words text-[15px] leading-relaxed text-[#333] dark:text-neutral-200">
              {renderMixedContent(post.content, { componentId: `talk-post-${post.id}` })}
            </div>

            {!inShell && (
              <MobileBottomToolRegistration
                tool={{
                  id: "talk-recommend",
                  kind: "action",
                  label: "추천",
                  icon: ThumbsUp,
                  order: 1,
                  ariaLabel: `추천하기 (현재 ${post.recommendCount})`,
                  status: String(post.recommendCount),
                  pending: isPending,
                  onAction: handleRecommend,
                }}
              />
            )}
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleRecommend}
                disabled={isPending}
                aria-label={`이 글 추천하기 (현재 ${post.recommendCount})`}
                className="inline-flex min-w-28 flex-col items-center gap-0.5 rounded-lg border-2 border-amber-400 bg-amber-50 px-5 py-2.5 text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-60 dark:border-amber-500 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-950/60"
              >
                <ThumbsUp className="size-5" aria-hidden />
                <span className="text-sm font-semibold">추천 {post.recommendCount}</span>
              </button>
            </div>
          </div>

          {canMutate && (
            <div className="flex justify-end gap-1 border-t border-gray-100 px-4 py-2.5 dark:border-neutral-800 sm:px-5">
              <button
                type="button"
                onClick={() => {
                  setTitle(post.title);
                  setContent(post.content);
                  setIsNotice(post.isNotice);
                  setError(null);
                  imageFileMapRef.current.clear();
                  setEditing(true);
                }}
                disabled={isPending}
                className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-[#706E6E] hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
              >
                <Pencil className="size-3.5" aria-hidden />
                수정
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isPending}
                className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
              >
                <Trash2 className="size-3.5" aria-hidden />
                삭제
              </button>
            </div>
          )}
        </>
      )}
    </article>
  );
}
